import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Building2, Eye, CalendarClock, Check, X } from "lucide-react";

const FLAG_KEYS = ["see_revenue_qty", "see_wallet", "see_visit_notes", "see_web_research", "see_ai_analysis"];
const VIS_LABEL = { all: "vis_all", assigned: "vis_assigned", assigned_team: "vis_team" };

export default function MyAccess() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/me/access").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-gray-400">{t("loading")}</p>;
  const p = data.perms || {};

  return (
    <div className="space-y-5" data-testid="my-access-page">
      <div className="bg-[#1B2A4A] rounded-lg p-6 text-white flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-[#F5A623] flex items-center justify-center shrink-0">
          <ShieldCheck className="h-6 w-6 text-[#1B2A4A]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("my_accounts_rights")}</h1>
          <p className="text-sm text-white/60 mt-0.5">
            {t("my_role")}: <span className="text-[#F5A623] font-semibold" data-testid="my-role">{data.profile_name}</span> ·{" "}
            {t("access_scope")}: {t(VIS_LABEL[p.account_visibility] || "vis_all")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
          <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><Eye className="h-4 w-4 text-gray-400" />{t("field_permissions")}</p>
          <div className="space-y-1.5" data-testid="my-field-perms">
            {FLAG_KEYS.map((k) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{t(k)}</span>
                {p[k] ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-400" />}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3 lg:col-span-2">
          <p className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-gray-400" />{t("active_grants")}</p>
          {(data.grants || []).length === 0 ? <p className="text-sm text-gray-400">{t("no_grants")}</p> : (
            <div className="divide-y divide-gray-100" data-testid="my-grants">
              {data.grants.map((g) => (
                <div key={g.id} className="py-2 flex items-center gap-3 text-sm">
                  <Badge className="bg-amber-50 text-amber-800 hover:bg-inherit">{t(g.type === "exception" ? "exception" : "deputy")}</Badge>
                  <span className="font-medium">{g.account_name || t("all_accounts_visible")}</span>
                  <span className="text-gray-400 ml-auto text-xs">{g.start_date || "…"} → {g.end_date || "∞"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <p className="text-sm font-semibold text-[#1B2A4A]">{t("visible_accounts")}</p>
          <Badge variant="outline" className="ml-2" data-testid="my-account-count">{data.account_count}</Badge>
          {data.all_visible && <span className="text-xs text-emerald-600 ml-auto">{t("all_accounts_visible")}</span>}
        </div>
        <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
          {(data.accounts || []).map((a) => (
            <button key={a.id} onClick={() => navigate(`/accounts/${a.id}`)} data-testid={`my-account-${a.id}`}
              className="w-full text-left px-5 py-2.5 flex items-center gap-3 text-sm hover:bg-amber-50/40 transition-colors duration-150">
              <span className="font-medium text-[#1B2A4A] min-w-[220px]">{a.name}</span>
              <span className="font-mono text-xs text-gray-400">{a.account_number}</span>
              <div className="flex flex-wrap gap-1 ml-auto">
                {(a.segments || []).map((s) => <Badge key={s} className="bg-amber-50 text-amber-800 hover:bg-inherit font-normal text-[10px]">{s}</Badge>)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
